import {
  Benefit,
  Consultation,
  ConsultationSimple,
  Package,
  PackSize,
  Product,
  ProductBack,
  ProductRecommendation,
  ProductRecommendationBack,
  SmartRecoApiBody,
  SRSApiBody,
} from '@app/core/models';
import { BreedSize, Helper, MeasureHelper, MeasurementCodeType, ProductPillar, ProductType, SpeciesCode } from '@app/shared/utils';
import * as objectPath from 'object-path';
import { SystemPreferenceCode } from '@app/shared/utils/enums/system-preference-code';

/**
 * No instance methods
 * Only static methods
 * No constructor for this class
 */
export class ServicesHelper {
  static mapProductBackToFront(productBack: ProductBack, currentSystemPreferenceCode: SystemPreferenceCode): Product {
    return {
      id: productBack.id,
      mainItemId: productBack.mainItem,
      productType: productBack.technology as ProductType,
      speciesCode: productBack.species,
      image: productBack.image,
      name: productBack.name,
      ean: productBack.ean,
      energy: productBack.referenceEnergyValue,
      energyCategory: productBack.energyCategory,
      programs: productBack.programs,
      lifestagesCode: productBack.lifestages,
      specificNeeds: productBack.specificNeeds || [],
      packages: this.getPackages(productBack, currentSystemPreferenceCode),
      composition: this.getComposition(productBack.originalProduct),
      benefits: this.getBenefits(productBack.originalProduct),
      weightCategoriesCode: this.getWeightCategoryCode(productBack),
      description: this.getDescription(productBack.originalProduct),
      rangeCategory: productBack.rangeCategory,
      range: productBack.range,
      retailChannel: productBack.retailChannel,
      recommendedFor: productBack.recommendedFors,
      notRecommendedFor: productBack.notRecommendedFors,
      territory: productBack.territory,
      relatedProductsCode: productBack.relatedProducts ? productBack.relatedProducts.filter((id) => !!id).map((id) => id.toString()) : [],
      density: productBack.density,
      pillar: productBack.pillar,
    };
  }

  static isProductComplete(productBack: ProductBack): boolean {
    const stringIsNull = (str: string) => !str || str === '';
    const vetWithTerritory = productBack.pillar === ProductPillar.VET ? !!productBack.territory : true;
    return (
      !!productBack.lifestages &&
      productBack.lifestages.length > 0 &&
      !!productBack.programs &&
      productBack.programs.length > 0 &&
      !!productBack.technology &&
      [ProductType.Dry, ProductType.Wet].includes(productBack.technology as ProductType) &&
      !!productBack.species &&
      !stringIsNull(productBack.name) &&
      !!productBack.referenceEnergyValue &&
      !!productBack.packs &&
      productBack.packs.filter((p) => !!p).length > 0 &&
      vetWithTerritory
    );
  }

  static mapSmartRecoApiBodyToSRSApiBody(data: SmartRecoApiBody): SRSApiBody {
    return {
      pet: {
        species: [data.pet.speciesCode],
        breed: [data.pet.breedCode],
        sterilized: data.pet.neutered,
        sensitivity: data.pet.sensitivity,
        dateOfBirth: data.pet.dateOfBirth,
        pathologies: data.pet.pathologies,
        bcs: data.pet.bcs,
        weight: data.pet.weight,
        ...(data.pet.speciesCode === SpeciesCode.Dog && { lifestagesDog: data.pet.lifestage }),
        ...(data.pet.speciesCode === SpeciesCode.Cat && { lifestagesCat: data.pet.lifestage }),
        reproductionStatus: data.pet.reproductionStatus,
      },
    };
  }

  static filterMapProductRecommendations(
    products: ProductRecommendationBack[],
    currentSystemPreferenceCode: SystemPreferenceCode,
  ): ProductRecommendation[] {
    if (!products) {
      return [];
    }
    return products
      .filter((pr) => this.isProductComplete(pr.product))
      .map((pr) => ({
        coverage: pr.coverage,
        product: this.mapProductBackToFront(pr.product, currentSystemPreferenceCode),
      }));
  }

  static filterMapProducts(products: ProductBack[], currentSystemPreferenceCode: SystemPreferenceCode): Product[] {
    return products
      .filter((product) => ServicesHelper.isProductComplete(product))
      .map((product) => ServicesHelper.mapProductBackToFront(product, currentSystemPreferenceCode));
  }

  static isPackComplete(pack: PackSize): boolean {
    return !!pack.weight && !!pack.measurementUnitCode && !!pack.ean && !!pack.sCode;
  }

  static getPackages(productBack: ProductBack, currentSystemPreferenceCode: SystemPreferenceCode): Package[] {
    if (!productBack.packs) {
      return [];
    }

    const packages = productBack.packs.reduce((packs: Package[], pack: PackSize): Package[] => {
      if (!pack || !this.isPackComplete(pack)) {
        return packs;
      }
      const weight = +pack.weight.toString().replace(',', '.');
      const weightInG = MeasureHelper.convertWeight(weight, pack.measurementUnitCode, MeasurementCodeType.Gram).measure;
      const convertedWeightInVetPreference = MeasureHelper.convertWeightWithCustomMapping(
        +weight,
        pack.measurementUnitCode,
        null,
        currentSystemPreferenceCode,
      );
      const convertedWeightTxt = MeasureHelper.weightToString(convertedWeightInVetPreference);

      convertedWeightTxt &&
        packs.push({
          sCode: pack.sCode,
          ean: pack.ean,
          type: pack.type,
          weight: weight,
          weightInG,
          text: convertedWeightTxt,
          convertedWeight: convertedWeightInVetPreference,
          measurementUnitCode: pack.measurementUnitCode,
          productPrice: pack.productPrice,
        });
      return packs;
    }, []);
    return this.sortPackages(packages);
  }

  static sortPackages(packages: Package[]): Package[] {
    return packages.sort((packSize1, packSize2) => {
      return packSize1.weightInG - packSize2.weightInG;
    });
  }

  static getWeightCategoryCode(productBack: ProductBack): BreedSize[] {
    return productBack.sizes && productBack.sizes.map(Helper.cleanSizeCategory);
  }

  static getBenefits(productWithDetails: any): Benefit[] {
    // check if I called original product
    if (!productWithDetails) {
      return [];
    }

    const benefits: Benefit[] = [];
    productWithDetails.Contents.filter((content) => content.Title.toLowerCase() === 'benefits').forEach((content) => {
      content.Content.forEach((keyContent) => {
        const key = Object.keys(keyContent)[0];
        return benefits.push({
          title: key,
          id: keyContent[key].asset_id,
          description: keyContent[key].Description,
          image: keyContent[key].Image,
        });
      });
    });
    return benefits;
  }

  static getComposition(productWithDetails: any): string[] {
    // check if I called original product
    if (!productWithDetails) {
      return [];
    }
    const composition = [];
    productWithDetails.Contents.filter((content) => content.Title.toLowerCase() === 'compositions')
      .map((content) => {
        content.Content.map((keyContent) => {
          const key = Object.keys(keyContent)[0];
          return composition.push(keyContent[key]);
        });
      })
      .filter((item) => !!item);
    return [...this.getCompositionExtraInfos(productWithDetails), ...composition];
  }

  static getCompositionExtraInfos(productWithDetails: any): string[] {
    if (!productWithDetails) {
      return [];
    }
    return Object.keys(productWithDetails.metadata.Details)
      .map((detailKey: string) => {
        if (['legal text heading', 'parnuts statement'].includes(detailKey.toLowerCase())) {
          return productWithDetails.metadata.Details[detailKey];
        }
      })
      .filter((info) => !!info);
  }

  static getDescription(productWithDetails: any): string[] {
    if (!productWithDetails) {
      return [];
    }
    return productWithDetails.Contents.filter((content) => content.Title === 'Text').map((content) => {
      return content.Content.map((keyContent) => {
        const key = Object.keys(keyContent)[0];
        if (key === 'Prescriber Description' || key === 'EretailShort Description' || key === 'EretailLong Description') {
          return keyContent[key];
        }
      })[0];
    });
  }

  static consultationSimple(consultation: Consultation): ConsultationSimple {
    const nutritionData = objectPath.get(consultation, ['visit', 'recommendation', 'nutritionData']);
    if (!nutritionData) {
      return {
        date: consultation.date,
        productNames: [],
        ration: 0,
        weight: null,
        bcs: null,
        comments: '',
        consultation: consultation,
      };
    }

    const productNames = nutritionData.map((data) => objectPath.get(data, ['product', 'productDetail', 'details', 'name']));

    const ration = objectPath.get(consultation, ['visit', 'recommendation', 'diagnostic', 'kCalAllocation']);

    return {
      date: consultation.date,
      productNames,
      ration,
      weight: consultation.visit.weight,
      bcs: consultation.visit.weight?.bcs,
      comments: consultation.comments,
      consultation: consultation,
    };
  }
}
